import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import Switch from 'ext-ui/components/Switch/Switch'
import ExtUITextInput from 'ext-ui/components/TextInput/TextInput'
import React, {
  FormEvent,
  ReactElement,
  useEffect,
  useId,
  useState,
} from 'react'
import ConnectPhysicalCardModal from 'src/component-modal/ConnectPhysicalCardModal/ConnectPhysicalCardModal'
import DisconnectPhysicalCardModal from 'src/component-modal/DisconnectPhysicalCardModal/DisconnectPhysicalCardModal'
import { useFormFields, usePrevious } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  CreditCardListModel,
  CreditCardStatus,
  PhysicalCardStatus,
} from 'src/models/creditCard.models'
import { stringIsEmpty } from 'src/utils/validation/string.validation'

import SettingsControlRow from '../SettingsControlRow/SettingsControlRow'
import SettingsDisclosure from '../SettingsDisclosure/SettingsDisclosure'
import SettingsTile from '../SettingsTile/SettingsTile'
import style from './CardSettingsTile.style'

export type UpdatedBillingDetails = {
  /** Design's "Name line 1". */
  displayName: string
  /** Design's "Name line 2" — the company shown on the card. */
  companyName: string
  country: string
  address1: string
  address2: string
  city: string
  province: string
  postal: string
}

/** Shape produced by `toCountryOptions` — resolved by the page, passed in. */
export type CountryOption = { value: string; details: { text: string } }

type CardSettingsTileProps = {
  creditCard: CreditCardListModel
  /** Localized country list, resolved by the page. */
  countryOptions: CountryOption[]
  /** Read-only phone shown under the address. Omitted when there is none. */
  contactPhone?: string
  /** `canUpdateCreditCard`. When false the billing disclosure is read-only. */
  canEditCardDetails: boolean
  /** `PHYSICAL_CARD_TRANSACTIONS_FEED` — required to *connect* the feed. */
  canConnectPhysicalCardFeed: boolean
  /** Brand gate for the connected-capabilities disclosure. */
  showConnectedCapabilities: boolean
  /** Persists the billing address + card name (PUT /creditcards/:id). Omit for
   *  a read-only / preview render. */
  onSaveBillingDetails?: (details: UpdatedBillingDetails) => void
  isSavingBillingDetails?: boolean
  billingDetailsSaveError?: boolean
  /** Fired after the physical-card feed enroll flow completes, so the page can
   *  refresh card state and surface its own confirmation. */
  onPhysicalCardFeedConnected?: () => void
  /** Fired after the physical-card feed is disconnected. */
  onPhysicalCardFeedDisconnected?: () => void
}

type CapabilityKey = 'physicalFeed' | 'virtualCards'

function countryLabelFor(countryOptions: CountryOption[], iso: string): string {
  return (
    countryOptions.find((option) => option.value === iso)?.details.text ?? iso
  )
}

function BillingRow(props: {
  creditCard: CreditCardListModel
  countryOptions: CountryOption[]
  contactPhone?: string
  onSave?: (details: UpdatedBillingDetails) => void
  isSaving: boolean
  saveError: boolean
}): ReactElement {
  const {
    creditCard,
    countryOptions,
    contactPhone,
    onSave,
    isSaving,
    saveError,
  } = props
  const translate = useTranslate()
  const countrySelectId = useId()
  const address = creditCard.address

  // Shared form-field validation (useFormFields + the string validators
  // AddressForm/AccountInformationBox use) so required fields are enforced
  // before we PUT.
  const {
    fields,
    fieldsErrors,
    onFieldChange,
    setErrorsAndReturnDidPassValidation,
  } = useFormFields<UpdatedBillingDetails>(
    {
      displayName: creditCard.displayName ?? '',
      companyName: creditCard.companyName ?? '',
      country: address?.country ?? '',
      address1: address?.address1 ?? '',
      address2: address?.address2 ?? '',
      city: address?.city ?? '',
      province: address?.province ?? '',
      postal: address?.postal ?? '',
    },
    () => ({
      displayName: [stringIsEmpty()],
      country: [stringIsEmpty()],
      address1: [stringIsEmpty()],
      city: [stringIsEmpty()],
      province: [stringIsEmpty()],
      postal: [stringIsEmpty()],
      // Name line 2 (company) and apt/suite are optional per the design.
      companyName: [],
      address2: [],
    }),
  )

  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle')

  // Reflect the put's outcome once it transitions from in-flight to done.
  const prevSaving = usePrevious(isSaving)
  useEffect(() => {
    if (prevSaving && !isSaving) {
      setStatus(saveError ? 'error' : 'saved')
    }
  }, [prevSaving, isSaving, saveError])

  const changeField = (
    field: keyof UpdatedBillingDetails,
    value: string,
  ): void => {
    onFieldChange(field, value)
    setStatus('idle')
  }

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault()
    setStatus('idle')
    if (!setErrorsAndReturnDidPassValidation()) {
      return
    }
    onSave?.({ ...fields })
  }

  return (
    <SettingsDisclosure
      title={translate('Billing address')}
      summary={countryLabelFor(countryOptions, fields.country)}
      actionLabel={translate('Edit')}
    >
      <form
        className="CardSettingsTile-form"
        onSubmit={handleSubmit}
        noValidate
      >
        {/* Design's two-line "Official display name" group. Line 1 maps to the
            card's displayName and line 2 to companyName — the same pair the
            legacy AccountInformationBox saves, so both persist via the PUT. */}
        <div className="CardSettingsTile-fieldGroup">
          <div className="CardSettingsTile-fieldGroupTitle">
            {translate('Official display name')}
          </div>
          <p className="CardSettingsTile-fieldGroupDescription">
            {translate(
              'The name and company shown on this card. Editing does not reissue the card.',
            )}
          </p>
          <ExtUITextInput
            variant="small"
            label={translate('Name line 1')}
            requirement="required"
            value={fields.displayName}
            errorText={fieldsErrors.displayName}
            onChange={(event) => changeField('displayName', event.target.value)}
          />
          <ExtUITextInput
            variant="small"
            label={translate('Name line 2')}
            requirement="optional"
            value={fields.companyName}
            onChange={(event) => changeField('companyName', event.target.value)}
          />
        </div>
        <div className="CardSettingsTile-field">
          <label className="CardSettingsTile-label" htmlFor={countrySelectId}>
            {translate('Country')}
          </label>
          <select
            id={countrySelectId}
            className="CardSettingsTile-select"
            value={fields.country}
            onChange={(event) => changeField('country', event.target.value)}
          >
            {countryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.details.text}
              </option>
            ))}
          </select>
        </div>
        <ExtUITextInput
          variant="small"
          label={translate('Street address')}
          requirement="required"
          value={fields.address1}
          errorText={fieldsErrors.address1}
          onChange={(event) => changeField('address1', event.target.value)}
        />
        <ExtUITextInput
          variant="small"
          label={translate('Apt, suite, etc.')}
          requirement="optional"
          value={fields.address2}
          onChange={(event) => changeField('address2', event.target.value)}
        />
        <div className="CardSettingsTile-fieldRow">
          <ExtUITextInput
            variant="small"
            label={translate('City')}
            requirement="required"
            value={fields.city}
            errorText={fieldsErrors.city}
            onChange={(event) => changeField('city', event.target.value)}
          />
          <ExtUITextInput
            variant="small"
            label={translate('State')}
            requirement="required"
            value={fields.province}
            errorText={fieldsErrors.province}
            onChange={(event) => changeField('province', event.target.value)}
          />
          <ExtUITextInput
            variant="small"
            label={translate('ZIP')}
            requirement="required"
            value={fields.postal}
            errorText={fieldsErrors.postal}
            onChange={(event) => changeField('postal', event.target.value)}
          />
        </div>
        {/* The card carries no phone field, so the page passes the signed-in
            user's real number for a read-only render. Omitted entirely when
            there is none — never a placeholder. */}
        {contactPhone !== undefined && (
          <ExtUITextInput
            variant="small"
            label={translate('Mobile phone')}
            value={contactPhone}
            readOnly
            microcopy={translate(
              'The phone number on file for your account. Update it in your profile.',
            )}
          />
        )}
        <div className="CardSettingsTile-formActions">
          <Button
            type="submit"
            dropShadow={false}
            loading={isSaving}
            data-testid="billing-save"
          >
            {translate('Save details')}
          </Button>
          {status === 'saved' && (
            <span className="CardSettingsTile-savedNote">
              {translate('Saved')}
            </span>
          )}
          {status === 'error' && (
            <span
              className="CardSettingsTile-errorNote"
              role="alert"
              data-testid="billing-save-error"
            >
              {translate("We couldn't save your changes. Please try again.")}
            </span>
          )}
        </div>
      </form>
    </SettingsDisclosure>
  )
}

/**
 * Billing address without `canUpdateCreditCard` — the legacy
 * AccountBillingInformationBox drops its edit affordance entirely in this case,
 * so offering a form (and a Save that the API would reject) would be wrong.
 */
function BillingReadOnlyRow(props: {
  creditCard: CreditCardListModel
  countryOptions: CountryOption[]
  contactPhone?: string
}): ReactElement {
  const { creditCard, countryOptions, contactPhone } = props
  const translate = useTranslate()
  const address = creditCard.address

  const rows: { label: string; value: string }[] = [
    {
      label: translate('Official display name'),
      value: creditCard.displayName,
    },
    ...(creditCard.companyName
      ? [{ label: translate('Company'), value: creditCard.companyName }]
      : []),
    {
      label: translate('Country'),
      value: countryLabelFor(countryOptions, address?.country ?? ''),
    },
    { label: translate('Street address'), value: address?.address1 ?? '' },
    ...(address?.address2
      ? [{ label: translate('Apt, suite, etc.'), value: address.address2 }]
      : []),
    { label: translate('City'), value: address?.city ?? '' },
    { label: translate('State'), value: address?.province ?? '' },
    { label: translate('ZIP'), value: address?.postal ?? '' },
    ...(contactPhone !== undefined
      ? [{ label: translate('Mobile phone'), value: contactPhone }]
      : []),
  ]

  return (
    <SettingsDisclosure
      title={translate('Billing address')}
      summary={countryLabelFor(countryOptions, address?.country ?? '')}
      actionLabel={translate('View')}
    >
      <p
        className="CardSettingsTile-readOnlyNote"
        data-testid="billing-read-only"
      >
        {translate(
          'You do not have permission to edit this account. Contact an Admin or Owner to make changes.',
        )}
      </p>
      <dl className="CardSettingsTile-kvList">
        {rows.map((row) => (
          <div key={row.label} className="CardSettingsTile-kv">
            <dt className="CardSettingsTile-kvLabel">{row.label}</dt>
            <dd className="CardSettingsTile-kvValue">{row.value}</dd>
          </div>
        ))}
      </dl>
    </SettingsDisclosure>
  )
}

type CapabilityRow = {
  key: CapabilityKey
  title: string
  description: string
  connected: boolean
  disabled: boolean
  onToggle: (checked: boolean) => void
}

function CapabilitiesRow(props: {
  creditCard: CreditCardListModel
  canConnectPhysicalCardFeed: boolean
  onConnected?: () => void
  onDisconnected?: () => void
}): ReactElement {
  const {
    creditCard,
    canConnectPhysicalCardFeed,
    onConnected,
    onDisconnected,
  } = props
  const translate = useTranslate()

  const physicalStatus = creditCard.physicalCard?.status ?? null
  // The card only has the physical-card feed capability when a physical card
  // exists and isn't closed — mirrors PhysicalCardTransactionsPanel's gating.
  const hasPhysicalCapability =
    physicalStatus !== null &&
    physicalStatus !== PhysicalCardStatus.NotApplicable &&
    physicalStatus !== PhysicalCardStatus.Closed
  const virtualCardsSupported = Boolean(
    creditCard.partner?.virtualCardsSupported,
  )

  const [physicalFeedConnected, setPhysicalFeedConnected] = useState(
    physicalStatus === PhysicalCardStatus.Active,
  )
  const [showConnectModal, setShowConnectModal] = useState(false)
  const [showDisconnectModal, setShowDisconnectModal] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)

  // The switch is operable only when the card actually has a physical card
  // AND — for *connecting* — the user holds the PHYSICAL_CARD_TRANSACTIONS_FEED
  // entitlement. An already-connected feed stays operable without it so the
  // user can still disconnect, matching PhysicalCardTransactionsPanel's escape
  // hatch. Without this the enroll flow would be reachable by anyone.
  const physicalDisabled =
    !hasPhysicalCapability ||
    (!physicalFeedConnected && !canConnectPhysicalCardFeed)

  // Virtual cards are READ-ONLY here. There is no enable/disconnect mutation
  // wired for this capability, so the switch reflects the card's real state
  // (mirroring VirtualCardCapabilitiesPanel.determineInitialConnectedStatus)
  // and is always disabled — toggling it would otherwise report a capability
  // as enabled when nothing was actually changed.
  const virtualCardsConnected =
    virtualCardsSupported &&
    creditCard.partner?.status === CreditCardStatus.Active

  const handlePhysicalToggle = (checked: boolean): void => {
    if (physicalDisabled) return
    if (checked) {
      setShowConnectModal(true)
    } else {
      setShowDisconnectModal(true)
    }
  }

  // No-op: the virtual cards row is read-only until an enable flow exists.
  const handleVirtualCardsToggle = (): void => undefined

  const rows: CapabilityRow[] = [
    {
      key: 'physicalFeed',
      title: translate('Physical card feed'),
      description: translate(
        'Import physical card transactions into the activity and transaction views.',
      ),
      connected: physicalFeedConnected,
      disabled: physicalDisabled,
      onToggle: handlePhysicalToggle,
    },
    {
      key: 'virtualCards',
      title: translate('Virtual cards'),
      description: translate(
        'Allow virtual cards to be created on this physical card — to control spend, reduce risk, and simplify vendor payments.',
      ),
      connected: virtualCardsConnected,
      disabled: true,
      onToggle: handleVirtualCardsToggle,
    },
  ]

  return (
    <SettingsDisclosure
      title={translate('Connected capabilities')}
      actionLabel={translate('Manage')}
      summary={`${translate('Physical card feed')} ${
        physicalFeedConnected ? translate('on') : translate('off')
      } · ${translate('Virtual cards')} ${
        virtualCardsConnected ? translate('on') : translate('off')
      }`}
    >
      {rows.map((row) => (
        <SettingsControlRow
          key={row.key}
          title={row.title}
          description={row.description}
        >
          <Pill size="small" color={row.connected ? 'highlight' : 'pending'}>
            {row.connected ? translate('Connected') : translate('Disconnected')}
          </Pill>
          <Switch
            checked={row.connected}
            disabled={row.disabled}
            ariaLabel={row.title}
            onCheckedChange={row.onToggle}
            data-testid={`capability-${row.key}`}
          />
        </SettingsControlRow>
      ))}

      <ConnectPhysicalCardModal
        isOpen={showConnectModal}
        onClose={() => {
          if (isVerifying) {
            return
          }
          setShowConnectModal(false)
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        reviewEnrollCardProps={{
          onBack: () => setShowConnectModal(false),
          creditCardId: creditCard.id,
          onVerifyStart: () => setIsVerifying(true),
          onVerifyComplete: () => {
            setIsVerifying(false)
            setShowConnectModal(false)
            setPhysicalFeedConnected(true)
            onConnected?.()
          },
          onVerifyError: () => setIsVerifying(false),
          submissionData: {
            binData: {
              id: creditCard.issuerId,
              eligibility: 'PHYSICAL_ONLY',
              issuerLogoImg: creditCard.cardImage.urls.small,
              issuerRequiresVerification: false,
            },
            addressData: {
              cardName: creditCard.displayName,
              ...creditCard.address,
            },
            cardData: {
              number: `**** **** **** ${creditCard.last4}`,
              cvc: '***',
              expiration: creditCard.expiresFormattedString,
            },
          },
        }}
      />
      <DisconnectPhysicalCardModal
        isOpen={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onDisconnectComplete={() => {
          setShowDisconnectModal(false)
          setPhysicalFeedConnected(false)
          onDisconnected?.()
        }}
        creditCardId={creditCard.id}
      />
    </SettingsDisclosure>
  )
}

function IssuerDetailsRow(props: {
  creditCard: CreditCardListModel
}): ReactElement {
  const { creditCard } = props
  const translate = useTranslate()

  // The design mocks "BIN" and "Network" rows, but the credit card model
  // carries neither: there is no BIN field (only `last4`), and `issuerName` is
  // the issuing bank, not the card network. Label each row for the data we
  // actually have rather than mislabelling it.
  const rows: { label: string; value: string }[] = [
    { label: translate('Card number'), value: `•••• ${creditCard.last4}` },
    { label: translate('Issuer'), value: creditCard.issuerName },
    {
      label: translate('Expiration'),
      value: creditCard.expiresFormattedString,
    },
  ]

  return (
    <SettingsDisclosure
      title={translate('Issuer details')}
      summary={translate('Read-only')}
      actionLabel={translate('View')}
    >
      <dl className="CardSettingsTile-kvList">
        {rows.map((row) => (
          <div key={row.label} className="CardSettingsTile-kv">
            <dt className="CardSettingsTile-kvLabel">{row.label}</dt>
            <dd className="CardSettingsTile-kvValue">{row.value}</dd>
          </div>
        ))}
      </dl>
    </SettingsDisclosure>
  )
}

/**
 * "Card settings" tile (EX-40354) — billing address, connected capabilities,
 * and read-only issuer details. Props-only: the page resolves the country list,
 * the permission/entitlement/brand gates, and the write, and owns the cache
 * invalidation + confirmation toast for the capability flows.
 */
export default function CardSettingsTile(
  props: CardSettingsTileProps,
): ReactElement {
  const {
    creditCard,
    countryOptions,
    contactPhone,
    canEditCardDetails,
    canConnectPhysicalCardFeed,
    showConnectedCapabilities,
    onSaveBillingDetails,
    isSavingBillingDetails = false,
    billingDetailsSaveError = false,
    onPhysicalCardFeedConnected,
    onPhysicalCardFeedDisconnected,
  } = props
  const translate = useTranslate()

  return (
    <SettingsTile title={translate('Card settings')}>
      <div className="CardSettingsTile" css={style}>
        {canEditCardDetails ? (
          <BillingRow
            creditCard={creditCard}
            countryOptions={countryOptions}
            contactPhone={contactPhone}
            onSave={onSaveBillingDetails}
            isSaving={isSavingBillingDetails}
            saveError={billingDetailsSaveError}
          />
        ) : (
          <BillingReadOnlyRow
            creditCard={creditCard}
            countryOptions={countryOptions}
            contactPhone={contactPhone}
          />
        )}
        {showConnectedCapabilities && (
          <CapabilitiesRow
            creditCard={creditCard}
            canConnectPhysicalCardFeed={canConnectPhysicalCardFeed}
            onConnected={onPhysicalCardFeedConnected}
            onDisconnected={onPhysicalCardFeedDisconnected}
          />
        )}
        <IssuerDetailsRow creditCard={creditCard} />
      </div>
    </SettingsTile>
  )
}
